import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { ApiError } from '@/api/client';
import { uploadReference } from '@/api/generation';
import { track } from '@/utils/tracker';
import { pushEvent } from '@/hooks/useAnalytics';
import { shouldUseLastResultAsReference } from '@/pages/generate/utils/chatIntent';
import { randomUUID } from '@/utils/uuid';
import ChatMessageItem, { type ChatMessage } from './ChatMessageItem';
import ChatInputBar from './ChatInputBar';
import WelcomeCard from './WelcomeCard';
import { useGenerationTasks } from '@/contexts/generationTaskStore';
import {
  isActiveChatTask,
  isChatTask,
  selectVisibleChatTasks,
} from '@/pages/generate/utils/chatTaskProjection';

interface GenerateResult {
  imageUrl: string;
  thumbnailUrl?: string;
  styleName: string;
  ratio: string;
  prompt?: string;
  gen_id?: string;
  favorited?: boolean;
}

export interface ChatSubmissionRejection {
  id: string;
  clientRequestId: string;
  prompt: string;
  code: string;
  discardClientRequestId?: string;
}

interface ChatViewProps {
  onSend: (prompt: string, refImageUrls?: string[], clientRequestId?: string) => void;
  onRegenerate: () => void;
  isGenerating: boolean;
  activeGenerationId?: string | null;
  result: GenerateResult | null;
  config: {
    style: string | null;
    ratio: string | null;
    model: string;
    quality: string;
    template: string | null;
  };
  configLabels: {
    style: string;
    ratio: string;
    model: string;
    quality: string;
    template: string;
  };
  onOpenConfig: (type: string) => void;
  onAIHelp?: (currentPrompt: string) => Promise<string | null>;
  aiHelpProfile?: string | null;
  aiHelpDisabled: boolean;
  user: { isVip?: boolean } | null;
  pointsCost: number | null;
  pointsBalance?: number;
  imageEditPointsCost?: number | null;
  onReferenceImageCountChange?: (count: number) => void;
  initialPrompt?: string;
  submissionRejection?: ChatSubmissionRejection | null;
  onViewHistory?: () => void;
}

interface SelectedChatImage {
  id: string;
  previewUrl: string;
  url?: string;
  uploading: boolean;
}

const MAX_CHAT_IMAGES = 5;

export default function ChatView({
  onSend,
  onRegenerate,
  isGenerating,
  activeGenerationId = null,
  result,
  config,
  configLabels,
  onOpenConfig,
  onAIHelp,
  aiHelpProfile,
  aiHelpDisabled,
  user,
  pointsCost,
  pointsBalance = 0,
  imageEditPointsCost,
  onReferenceImageCountChange,
  initialPrompt,
  submissionRejection,
  onViewHistory,
}: ChatViewProps) {
  const { t } = useTranslation();
  const { tasks, activeCount, activeLimit, capacityReady } = useGenerationTasks();
  const [messages, setMessages] = useState<ChatMessage[]>(() => []);
  const [inputValue, setInputValue] = useState('');
  const [selectedImages, setSelectedImages] = useState<SelectedChatImage[]>([]);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [aiHelpLoading, setAiHelpLoading] = useState(false);
  const [aiHelpError, setAiHelpError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewUrlsRef = useRef<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastResultRef = useRef<string | null>(null);
  const hasAddedLoading = useRef(false);
  const handledRejection = useRef<string | null>(null);
  const capacityFull = capacityReady && activeLimit > 0 && activeCount >= activeLimit;
  const visibleChatTasks = useMemo(
    () => selectVisibleChatTasks(tasks, [activeGenerationId, result?.gen_id]),
    [activeGenerationId, result?.gen_id, tasks],
  );
  const taskStoreResult = useMemo<GenerateResult | null>(() => {
    const task = [...visibleChatTasks]
      .reverse()
      .find((item) => item.status === 'success' && !!item.image_url);
    if (!task?.image_url) return null;
    return {
      imageUrl: task.image_url,
      thumbnailUrl: task.thumbnail_url,
      styleName: '',
      ratio: task.ratio,
      prompt: task.prompt,
      gen_id: task.gen_id,
      favorited: task.favorited,
    };
  }, [visibleChatTasks]);
  // ACK-bounded generation completes through TaskStore. Keep the direct result
  // projection for legacy foreground callers, but let an async terminal task
  // supply the same continuation contract after the page has released the ACK.
  const effectiveResult = result ?? taskStoreResult;
  const terminalChatTaskCount = useMemo(
    () => tasks.filter((task) => isChatTask(task) && !isActiveChatTask(task)).length,
    [tasks],
  );
  const shouldContinueFromLastResult = useMemo(() => shouldUseLastResultAsReference({
    text: inputValue.trim(),
    hasResult: !!effectiveResult,
    hasUploadedImages: selectedImages.length > 0,
    hasPriorImageMessages: messages.some((message) => message.type === 'image'),
  }), [effectiveResult, inputValue, messages, selectedImages.length]);
  const effectiveReferenceImageCount = selectedImages.length > 0
    ? selectedImages.length
    : shouldContinueFromLastResult
      ? 1
      : 0;

  useEffect(() => {
    onReferenceImageCountChange?.(effectiveReferenceImageCount);
  }, [effectiveReferenceImageCount, onReferenceImageCountChange]);

  useEffect(() => {
    if (initialPrompt && messages.length === 0) {
      setInputValue(initialPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt]);

  useEffect(() => {
    setAiHelpLoading(false);
    setAiHelpError(null);
  }, [aiHelpProfile]);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const previewUrls = previewUrlsRef.current;
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      previewUrls.clear();
    };
  }, []);

  useEffect(() => {
    if (isGenerating && !hasAddedLoading.current) {
      hasAddedLoading.current = true;
      setMessages((prev) => [
        ...prev,
        { id: `loading_${Date.now()}`, role: 'assistant', type: 'loading' },
      ]);
    } else if (!isGenerating) {
      hasAddedLoading.current = false;
      if (!result) {
        setMessages((prev) => (
          prev.some((m) => m.type === 'loading')
            ? prev.filter((m) => m.type !== 'loading')
            : prev
        ));
      }
    }
  }, [isGenerating, result]);

  useEffect(() => {
    const visibleIds = new Set(visibleChatTasks.map((task) => task.gen_id));
    if (activeGenerationId) visibleIds.add(activeGenerationId);
    if (result?.gen_id) visibleIds.add(result.gen_id);
    setMessages((previous) => {
      const next = previous.filter((message) => !message.genId || visibleIds.has(message.genId));
      for (const task of visibleChatTasks) {
        if (task.client_request_id) {
          const submissionIndex = next.findIndex(
            (message) => message.id === `submission_${task.client_request_id}`,
          );
          if (submissionIndex >= 0) next.splice(submissionIndex, 1);
        }
        const promptId = `prompt_${task.gen_id}`;
        if (!next.some((message) => message.id === promptId)) {
          const optimisticIndex = next.findIndex((message) => (
            message.role === 'user'
            && message.type === 'text'
            && !message.genId
            && (
              (task.client_request_id && message.clientRequestId === task.client_request_id)
              || (!task.client_request_id && message.content === task.prompt)
            )
          ));
          if (optimisticIndex >= 0) {
            next[optimisticIndex] = {
              ...next[optimisticIndex],
              id: promptId,
              genId: task.gen_id,
              clientRequestId: task.client_request_id,
            };
          } else {
            next.push({
              id: promptId,
              role: 'user',
              type: 'text',
              content: task.prompt,
              genId: task.gen_id,
              clientRequestId: task.client_request_id,
            });
          }
        }
        const id = `task_${task.gen_id}`;
        const index = next.findIndex(
          (message) => message.id === id
            || (message.role === 'assistant' && message.genId === task.gen_id),
        );
        const message: ChatMessage = task.status === 'success' && task.image_url
          ? {
            id,
            role: 'assistant',
            type: 'image',
            imageUrl: task.image_url,
            thumbnailUrl: task.thumbnail_url,
            genId: task.gen_id,
            ratio: task.ratio,
            prompt: task.prompt,
            favorited: task.favorited,
          }
          : {
            id,
            role: 'assistant',
            type: 'task',
            genId: task.gen_id,
            status: task.status,
            reasonCode: task.reason_code,
            pointsState: task.points_state,
            prompt: task.prompt,
          };
        if (index >= 0) {
          next[index] = message;
          for (let duplicate = next.length - 1; duplicate >= 0; duplicate -= 1) {
            if (
              duplicate !== index
              && next[duplicate].role === 'assistant'
              && next[duplicate].genId === task.gen_id
            ) {
              next.splice(duplicate, 1);
            }
          }
        } else next.push(message);
      }
      return next;
    });
  }, [activeGenerationId, result?.gen_id, visibleChatTasks]);

  useEffect(() => {
    if (!submissionRejection || handledRejection.current === submissionRejection.id) return;
    handledRejection.current = submissionRejection.id;
    if (submissionRejection.code !== 'ACK_UNKNOWN') {
      setInputValue(submissionRejection.prompt);
    }
    setMessages((previous) => {
      const retained = submissionRejection.discardClientRequestId
        ? previous.filter(
          (item) => item.clientRequestId !== submissionRejection.discardClientRequestId,
        )
        : previous;
      const message = {
        id: `submission_${submissionRejection.clientRequestId}`,
        role: 'system',
        type: 'system',
        content: submissionRejection.code === 'GEN_ACTIVE_LIMIT'
          ? t('gen_capacityNotSubmitted', '任务未提交：并发容量已满，已有任务完成后可再次发送')
          : submissionRejection.code === 'ACK_UNKNOWN'
            ? t('gen_submissionConfirming', '正在确认任务是否已提交，请勿重复发送')
            : t('gen_submitNotAccepted', '任务未提交，请检查后重试'),
      } as const;
      const existing = retained.findIndex((item) => item.id === message.id);
      if (existing < 0) return [...retained, message];
      const next = [...retained];
      next[existing] = message;
      return next;
    });
  }, [submissionRejection, t]);

  useEffect(() => {
    if (effectiveResult) {
      const resultKey = effectiveResult.gen_id || effectiveResult.imageUrl;
      if (effectiveResult.gen_id && tasks.some((task) => task.gen_id === effectiveResult.gen_id)) return;
      const isSameResult = resultKey === lastResultRef.current;
      setMessages((prev) => {
        const withoutLoading = prev.filter((m) => m.type !== 'loading');
        if (isSameResult) return withoutLoading;
        return [
          ...withoutLoading,
          {
            id: `ai_${Date.now()}`,
            role: 'assistant',
            type: 'image',
            imageUrl: effectiveResult.imageUrl,
            thumbnailUrl: effectiveResult.thumbnailUrl,
            genId: effectiveResult.gen_id,
            styleName: effectiveResult.styleName,
            ratio: effectiveResult.ratio,
            prompt: effectiveResult.prompt,
            favorited: effectiveResult.favorited,
          },
        ];
      });
      if (!isSameResult) lastResultRef.current = resultKey;
    }
  }, [effectiveResult, tasks]);

  const addFiles = useCallback((files: File[]) => {
    if (!files.length || isGenerating) return;
    setImageUploadError(null);

    const availableSlots = Math.max(0, MAX_CHAT_IMAGES - selectedImages.length);
    const accepted = files.slice(0, availableSlots).filter((file) => file.type.startsWith('image/'));
    if (!accepted.length) return;
    if (files.length > availableSlots) {
      setImageUploadError(t('chat_imageLimit', { count: MAX_CHAT_IMAGES }));
    }
    accepted.forEach((file, index) => {
      const id = `chat_ref_${Date.now()}_${index}_${Math.random().toString(16).slice(2)}`;
      const previewUrl = URL.createObjectURL(file);
      previewUrlsRef.current.add(previewUrl);
      setSelectedImages((prev) => [...prev, { id, previewUrl, uploading: true }]);
      void uploadReference(file)
        .then((res) => {
          track.referenceUploadSuccess({ source: 'generate_chat', count: 1 });
          setSelectedImages((prev) => prev.map((image) => (
            image.id === id ? { ...image, url: res.url, uploading: false } : image
          )));
        })
        .catch((err) => {
          URL.revokeObjectURL(previewUrl);
          previewUrlsRef.current.delete(previewUrl);
          const msg = err instanceof ApiError ? err.message : t('gen_referenceUploadFailed');
          track.referenceUploadFail({ source: 'generate_chat', reason: err instanceof ApiError ? err.code : 'upload_failed' });
          setImageUploadError(msg);
          setSelectedImages((prev) => prev.filter((image) => image.id !== id));
        });
    });
  }, [isGenerating, selectedImages.length, t]);

  const handleFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    addFiles(files);
  }, [addFiles]);

  const openImagePicker = useCallback(() => {
    if (isGenerating) return;
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios' && window.__NATIVE_BRIDGE__?.pickPhotos) {
      window.__NATIVE_BRIDGE__.pickPhotos({ limit: MAX_CHAT_IMAGES - selectedImages.length })
        .then(({ images }) => {
          const files = images.map((image, index) => {
            const binary = atob(image.base64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            return new File([new Blob([bytes], { type: image.mimeType })], `reference-${index + 1}.jpg`, { type: image.mimeType });
          });
          addFiles(files);
        })
        .catch(() => {});
      return;
    }
    fileInputRef.current?.click();
  }, [addFiles, isGenerating, selectedImages.length]);

  const removeSelectedImage = useCallback((id: string) => {
    setSelectedImages((prev) => {
      const target = prev.find((image) => image.id === id);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
        previewUrlsRef.current.delete(target.previewUrl);
      }
      return prev.filter((image) => image.id !== id);
    });
  }, []);

  const handleSend = useCallback(() => {
    const text = inputValue.trim();
    const hasUploading = selectedImages.some((image) => image.uploading);
    if ((!text && selectedImages.length === 0) || hasUploading || capacityFull) return;

    const uploadedUrls = selectedImages.map((image) => image.url).filter((url): url is string => !!url);
    if (selectedImages.length > 0 && uploadedUrls.length !== selectedImages.length) return;

    const effectiveText = text || t('chat_defaultEditPrompt', '根据上传图片进行创作和修改');
    const clientRequestId = randomUUID();
    const previews = selectedImages.map((image) => image.previewUrl);

    setMessages((prev) => [
      ...prev,
      {
        id: `prompt_${clientRequestId}`,
        role: 'user',
        type: 'text',
        content: effectiveText,
        clientRequestId,
        referenceImages: previews,
      },
    ]);
    setInputValue('');
    setSelectedImages([]);

    const refUrls = uploadedUrls.length
      ? uploadedUrls
      : (shouldContinueFromLastResult && effectiveResult ? [effectiveResult.imageUrl] : undefined);
    onSend(effectiveText, refUrls, clientRequestId);
  }, [capacityFull, effectiveResult, inputValue, onSend, selectedImages, shouldContinueFromLastResult, t]);

  const handleAIHelp = useCallback(async () => {
    const currentPrompt = inputValue.trim();
    if (!currentPrompt || !onAIHelp || isGenerating || aiHelpDisabled || aiHelpLoading) return;
    setAiHelpLoading(true);
    setAiHelpError(null);
    try {
      const expandedPrompt = await onAIHelp(currentPrompt);
      if (expandedPrompt != null) setInputValue(expandedPrompt);
    } catch (err) {
      setAiHelpError(
        err instanceof ApiError
          ? err.message
          : t('gen_aiHelpFailed', 'AI 帮写失败，请稍后重试'),
      );
    } finally {
      setAiHelpLoading(false);
    }
  }, [aiHelpDisabled, aiHelpLoading, inputValue, isGenerating, onAIHelp, t]);

  const handleRegenerate = useCallback(() => {
    if (isGenerating) return;
    setMessages((prev) => [
      ...prev,
      {
        id: `sys_${Date.now()}`,
        role: 'system',
        type: 'text',
        content: t('chat_regenerating', '正在重新生成...'),
      },
    ]);
    onRegenerate();
  }, [isGenerating, onRegenerate, t]);

  const suggestionTags = [
    t('chat_insp1', '赛博朋克风格女孩'),
    t('chat_insp2', '国风水墨山水画'),
    t('chat_insp3', '可爱Q版动漫角色'),
    t('chat_insp4', '未来科幻城市'),
  ];

  const isWelcomeState = !isGenerating
    && !effectiveResult
    && messages.length === 0
    && visibleChatTasks.length === 0;
  const displayedPointsCost = selectedImages.length > 0 ? imageEditPointsCost ?? null : pointsCost;

  return (
    <div
      role="status"
      data-testid="chat-view-root"
      data-generation-status={effectiveResult?.gen_id ? 'success' : activeGenerationId ? 'generating' : 'ready'}
      data-generation-id={effectiveResult?.gen_id ?? activeGenerationId ?? ''}
      aria-label={effectiveResult?.gen_id
        ? `generation-result-${effectiveResult.gen_id}`
        : activeGenerationId
          ? 'generation-status-root'
          : 'generation-status-ready'}
      className="flex-1 min-h-0 flex flex-col overflow-hidden"
    >
      {activeGenerationId && (
        <span
          role="status"
          className="sr-only"
          data-testid="generation-active-marker"
          aria-label={`generation-active-${activeGenerationId}-generating`}
        />
      )}
      <span
        role="status"
        className="sr-only"
        data-testid="generation-points-balance"
        aria-label={`generation-points-balance-${pointsBalance}`}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />
      <div
        ref={scrollRef}
        data-testid="chat-message-flow"
        className="flex-1 min-h-0 overflow-y-auto ios-scroll"
      >
        <div className={`px-4 ${isWelcomeState ? 'pt-16 pb-8 min-h-full flex flex-col justify-center gap-5' : 'pt-4 pb-6'}`}>
          {!isWelcomeState && terminalChatTaskCount > 3 && (
            <div
              data-testid="chat-recent-memory-notice"
              className="mb-4 flex items-center gap-3 rounded-2xl border border-purple-100 bg-white/80 px-3.5 py-3"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-500">
                  <i className="ri-history-line text-sm" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-700">
                    {t('chat_recentMemoryTitle', '仅展示最近 3 条创作')}
                  </p>
                  <p className="mt-0.5 truncate text-[10px] text-gray-400">
                    {t('chat_recentMemoryDesc', '全部图片可在“我的-生成记录”查看')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  pushEvent('chat_recent_history_click');
                  onViewHistory?.();
                }}
                className="h-9 shrink-0 rounded-full bg-purple-50 px-3 text-[11px] font-semibold text-purple-600 active:bg-purple-100"
              >
                {t('chat_viewAllHistory', '查看全部')}
              </button>
            </div>
          )}
          {messages.map((msg, index) => (
            <ChatMessageItem
              key={msg.id}
              message={msg}
              onRegenerate={handleRegenerate}
              isLast={index === messages.length - 1}
              user={user}
            />
          ))}

          {isWelcomeState && <WelcomeCard />}

          {isWelcomeState && (
            <div className="px-1">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}>
                  <i className="ri-lightbulb-flash-line text-white text-xs" />
                </div>
                <span className="text-[13px] font-bold text-gray-700">{t('gen_inspiration')}</span>
                <div className="flex-1 h-px mx-2" style={{ background: 'linear-gradient(90deg, rgba(168,85,247,0.15), transparent)' }} />
                <button className="flex items-center gap-1 text-[11px] text-purple-500 cursor-pointer active:opacity-70 hover:text-purple-600 transition-colors font-medium">
                  <i className="ri-refresh-line text-xs" />
                  <span>{t('gen_refreshInspiration', '换一换')}</span>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                {suggestionTags.map((text, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setInputValue(text);
                    }}
                    className="min-w-0 h-11 px-3 rounded-2xl text-xs text-gray-600 cursor-pointer text-center leading-tight truncate transition-all duration-200 active:scale-[0.97]"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,245,250,0.9))',
                      border: '1px solid rgba(255,107,157,0.12)',
                      boxShadow: '0 1px 4px rgba(168,85,247,0.06)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(255,107,157,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(255,107,157,0.12)';
                    }}
                  >
                    {text}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div data-testid="chat-config-row" className="shrink-0 px-4 pt-2 pb-2.5 flex items-center gap-2.5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        <button
          onClick={() => onOpenConfig('ratio')}
          aria-label="generation-ratio-selector"
          className="flex items-center gap-1 rounded-full px-3 py-1.5 cursor-pointer active:scale-95 transition-all duration-200 flex-shrink-0"
          style={{
            background: config.ratio ? 'rgba(139,92,246,0.08)' : 'rgba(0,0,0,0.03)',
            border: config.ratio ? '1px solid rgba(139,92,246,0.15)' : '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <i className="ri-aspect-ratio-line text-[11px] text-gray-500" />
          <span className="text-[11px] font-medium text-gray-600 whitespace-nowrap">
            {configLabels.ratio || t('gen_ratioFallback')}
          </span>
          <i className="ri-arrow-down-s-line text-gray-400 text-[10px]" />
        </button>

        <button
          onClick={() => onOpenConfig('quality')}
          aria-label="generation-quality-selector"
          className="flex items-center gap-1 rounded-full px-3 py-1.5 cursor-pointer active:scale-95 transition-all duration-200 flex-shrink-0"
          style={{
            background: config.quality !== 'standard' ? 'rgba(245,158,11,0.08)' : 'rgba(0,0,0,0.03)',
            border: config.quality !== 'standard' ? '1px solid rgba(245,158,11,0.15)' : '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <i className="ri-image-2-line text-[11px] text-gray-500" />
          <span className="text-[11px] font-medium text-gray-600 whitespace-nowrap">
            {configLabels.quality || t(`gen_quality_${config.quality}_name`, {
              defaultValue: config.quality,
            })}
          </span>
          <i className="ri-arrow-down-s-line text-gray-400 text-[10px]" />
        </button>
      </div>

      <div data-testid="chat-input-area" className="shrink-0">
        <ChatInputBar
          value={inputValue}
          onChange={setInputValue}
          onSend={handleSend}
          disabled={selectedImages.some((image) => image.uploading)}
          submitDisabled={capacityFull}
          capacityMessage={capacityFull
            ? t('gen_capacityFullMessage', {
              active: activeCount,
              limit: activeLimit,
            })
            : null}
          onAIHelp={() => { void handleAIHelp(); }}
          aiHelpProfile={aiHelpProfile}
          aiHelpDisabled={aiHelpDisabled}
          aiHelpLoading={aiHelpLoading}
          aiHelpError={aiHelpError}
          pointsCost={displayedPointsCost}
          onImagePick={openImagePicker}
          selectedImages={selectedImages}
          onRemoveImage={removeSelectedImage}
          imageUploadError={imageUploadError}
          selectedModel={config.model}
          selectedModelName={configLabels.model}
          onOpenModel={() => onOpenConfig('model')}
        />
      </div>
    </div>
  );
}
